import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Space, Icon, Modal, useNotify, Table, Tooltip, Alert } from 'ebs-design';
import { Pagination } from 'components/organisms';
import { useCallsAPI } from 'api';
import { useMutation, useQueryClient } from 'react-query';

import { CallCategoryType, CallType, Column, FullCall, Properties, Results } from 'types';
import { Link } from 'react-router-dom';
import cn from 'classnames';
import { Call } from 'resources/icons';
import { AxiosError } from 'axios';

interface CallTypeModalProps {
  calls?: Results<FullCall>;
  queryKey?: string | string[];
  onClose?: () => void;
}

export const CallTypeModal: React.FC<CallTypeModalProps> = ({ calls, queryKey, onClose }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const notify = useNotify();

  const { editCall } = useCallsAPI();

  const {
    mutate: editCallMutate,
    isLoading,
    variables,
  } = useMutation(
    (data: { category: CallCategoryType; id: number }) =>
      editCall({
        id: data.id,
        data: { category: data.category },
      }),
    {
      onError: (e: AxiosError) => {
        const errorMessage = (e?.response as Properties)?.detail;
        notify.error({ title: errorMessage });
      },
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey || ['calls']);
      },
    },
  );

  const columns: Column<FullCall>[] = [
    {
      title: t('calls.from_phone_number'),
      dataIndex: 'from_phone_number',
    },
    {
      title: t('calls.to_phone_number'),
      dataIndex: 'to_phone_number',
    },
    {
      title: t('calls.fullName'),
      render: ({ type, from_phone_number, to_phone_number, contact }) => {
        const phoneNumber =
          type === CallType.INCOMING
            ? from_phone_number
            : type === CallType.OUTGOING
            ? to_phone_number
            : null;

        const display = contact
          ? [contact.first_name, contact.last_name].join(' ')
          : phoneNumber ?? '---';

        const truncateStyle: React.CSSProperties = {
          display: 'inline-block',
          maxWidth: 150,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          verticalAlign: 'middle',
        };

        const content = contact?.lead ? (
          <Link to={`/lead/${contact.lead}`} style={truncateStyle}>
            {display}
          </Link>
        ) : (
          <span style={truncateStyle}>{display}</span>
        );

        return (
          <Tooltip placement="top" tooltip={display}>
            {content}
          </Tooltip>
        );
      },
    },
    {
      title: t('calls.action'),
      align: 'right',
      render: (item) => (
        <Tooltip
          placement="bottom"
          trigger="click"
          bodyClass="no-padding"
          tooltip={
            <>
              <Button
                block
                type="text"
                className="mt-2"
                onClick={() => editCallMutate({ category: CallCategoryType.sales, id: item.id })}
              >
                {t('calls.sales')}
              </Button>

              <Button
                block
                type="text"
                onClick={() =>
                  editCallMutate({ category: CallCategoryType.outstanding, id: item.id })
                }
              >
                {t('calls.outstanding')}
              </Button>
            </>
          }
        >
          <Button
            type="primary"
            className={cn('ebs-button', { 'danger-button': true })}
            loading={isLoading && variables?.id === item.id}
          >
            <Space>
              <Icon component={Call} />

              {t('calls.select_type')}
            </Space>
          </Button>
        </Tooltip>
      ),
    },
  ];

  return (
    <Modal
      closeOnClickOutside={false}
      open
      header={<h3>{t('contacts.reAssignResponsible')}</h3>}
      onClose={onClose}
    >
      <Modal.Content>
        <Alert type="info" className="mb-10">
          <p>{t('calls.call_type_modal_info')}</p>
        </Alert>

        <div style={{ maxHeight: 400, overflowY: 'auto' }} className="mb-10">
          <Table
            columns={columns}
            data={calls?.results}
            rowKey="id"
            scroll={{ x: 'max-content' }}
          />
        </div>

        <Pagination size={calls?.total_pages || 0} />
      </Modal.Content>
    </Modal>
  );
};
